import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './IcsnConsolePage.css';

const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

function apiUrl(path) {
  return `${API_BASE}${path}`;
}

async function requestJson(path, options = {}) {
  const response = await fetch(apiUrl(path), {
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
    ...options,
  });
  const raw = await response.text();
  let parsed = {};
  try {
    parsed = raw ? JSON.parse(raw) : {};
  } catch {
    parsed = { raw };
  }
  if (!response.ok) {
    const detail = parsed?.detail || raw || `${response.status}`;
    throw new Error(detail);
  }
  return parsed;
}

function formatDateTime(value) {
  if (!value) {
    return '-';
  }
  try {
    const d = new Date(value);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    const ss = String(d.getSeconds()).padStart(2, '0');
    return `${y}-${m}-${day} ${hh}:${mm}:${ss}`;
  } catch {
    return String(value);
  }
}

function IcsnConsolePage() {
  const [runtime, setRuntime] = useState(null);
  const [health, setHealth] = useState(null);
  const [queueItems, setQueueItems] = useState([]);
  const [outboxItems, setOutboxItems] = useState([]);
  const [riskItems, setRiskItems] = useState([]);
  const [priorityItems, setPriorityItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [actionMessage, setActionMessage] = useState('');

  const [form, setForm] = useState({
    facility_id: 'FACILITY-DEMO-001',
    subject_id: 'SUBJECT-DEMO-001',
    reader_id: 'READER-DEMO-001',
    humidity_level: 45,
  });

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [runtimeRes, queueRes, outboxRes, riskRes, priorityRes, healthRes] = await Promise.all([
        requestJson('/api/v1/icsn/console/runtime'),
        requestJson('/api/v1/icsn/console/simulate/queue?limit=20'),
        requestJson('/api/v1/icsn/console/analysis/fhir/outbox?limit=20'),
        requestJson('/api/v1/icsn/console/analysis/risk-signals?limit=20'),
        requestJson('/api/v1/icsn/console/analysis/care-priority?status=open&limit=20'),
        requestJson('/api/v1/icsn/console/analysis/health'),
      ]);
      setRuntime(runtimeRes);
      setQueueItems(Array.isArray(queueRes?.items) ? queueRes.items : []);
      setOutboxItems(Array.isArray(outboxRes?.items) ? outboxRes.items : []);
      setRiskItems(Array.isArray(riskRes?.items) ? riskRes.items : []);
      setPriorityItems(Array.isArray(priorityRes?.items) ? priorityRes.items : []);
      setHealth(healthRes);
    } catch (e) {
      setError(e.message || '조회 실패');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
    const timer = setInterval(loadAll, 10000);
    return () => clearInterval(timer);
  }, [loadAll]);

  const updateRuntime = useCallback(async (payload) => {
    setActionMessage('');
    setError('');
    try {
      const updated = await requestJson('/api/v1/icsn/console/runtime', {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      setRuntime(updated);
      setActionMessage('런타임 상태를 업데이트했습니다.');
    } catch (e) {
      setError(e.message || '런타임 상태 업데이트 실패');
    }
  }, []);

  const injectSample = useCallback(async () => {
    setActionMessage('');
    setError('');
    try {
      const payload = {
        facility_id: form.facility_id,
        subject_id: form.subject_id,
        reader_id: form.reader_id,
        humidity_level: Number(form.humidity_level),
        sensor_mode: runtime?.sensor_mode || 'active',
      };
      const res = await requestJson('/api/v1/icsn/console/simulate/inject-sample', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      setActionMessage(
        res.queued
          ? `샘플 이벤트를 큐에 저장했습니다. event_id=${res.event_id}`
          : `샘플 이벤트를 전송했습니다. event_id=${res.event_id}`,
      );
      await loadAll();
    } catch (e) {
      setError(e.message || '샘플 주입 실패');
    }
  }, [form, loadAll, runtime?.sensor_mode]);

  const flushQueue = useCallback(async () => {
    setActionMessage('');
    setError('');
    try {
      const res = await requestJson('/api/v1/icsn/console/simulate/flush', {
        method: 'POST',
        body: JSON.stringify({
          created_by: 'console-ui',
          notes: 'manual flush from console ui',
        }),
      });
      setActionMessage(
        `재전송 완료: processed=${res.processed}, failed=${res.failed}, remaining=${res.remaining}, batch_id=${res.batch_id || '-'}`,
      );
      await loadAll();
    } catch (e) {
      setError(e.message || '큐 재전송 실패');
    }
  }, [loadAll]);

  const ackOutbox = useCallback(
    async (outboxId) => {
      setActionMessage('');
      setError('');
      try {
        await requestJson(`/api/v1/icsn/console/analysis/fhir/outbox/${outboxId}/ack`, { method: 'POST' });
        setActionMessage(`Outbox ACK 완료: ${outboxId}`);
        await loadAll();
      } catch (e) {
        setError(e.message || 'Outbox ACK 실패');
      }
    },
    [loadAll],
  );

  const ackPriority = useCallback(
    async (itemId) => {
      setActionMessage('');
      setError('');
      try {
        await requestJson(`/api/v1/icsn/console/analysis/care-priority/${itemId}/ack`, { method: 'POST' });
        setActionMessage(`Care Priority ACK 완료: ${itemId}`);
        await loadAll();
      } catch (e) {
        setError(e.message || 'Care Priority ACK 실패');
      }
    },
    [loadAll],
  );

  const runtimeBadge = useMemo(() => {
    if (!runtime) {
      return 'N/A';
    }
    return `${runtime.sensor_mode || 'unknown'} | outage=${runtime.simulate_outage ? 'ON' : 'OFF'} | queue=${runtime.queued_events || 0}`;
  }, [runtime]);

  return (
    <div className="icsn-console-page">
      <header className="icsn-console-header">
        <h1>iCSN 시나리오 콘솔</h1>
        <p>모드 전환, 샘플 주입, 단절 큐/재전송, Outbox/위험신호/우선순위 상태를 UI에서 직접 제어합니다.</p>
        <div className="icsn-console-meta">
          <span>Runtime: {runtimeBadge}</span>
          <span>Service: {health?.service || '-'}</span>
          <span>Health: {health?.status || '-'}</span>
          <button type="button" onClick={loadAll} disabled={loading}>
            새로고침
          </button>
        </div>
      </header>

      {error ? <div className="icsn-console-error">{error}</div> : null}
      {actionMessage ? <div className="icsn-console-message">{actionMessage}</div> : null}

      <section className="icsn-console-card">
        <h2>1) 런타임 제어</h2>
        <div className="icsn-console-row">
          <label htmlFor="mode-select">Sensor Mode</label>
          <select
            id="mode-select"
            value={runtime?.sensor_mode || 'active'}
            onChange={(e) => updateRuntime({ sensor_mode: e.target.value })}
          >
            <option value="active">active</option>
            <option value="passive">passive</option>
          </select>
          <button
            type="button"
            onClick={() => updateRuntime({ simulate_outage: !(runtime?.simulate_outage || false) })}
          >
            단절 시뮬레이션 {runtime?.simulate_outage ? '해제' : '활성화'}
          </button>
        </div>
      </section>

      <section className="icsn-console-card">
        <h2>2) 샘플 주입 / 재전송</h2>
        <div className="icsn-console-grid">
          <label>
            facility_id
            <input
              value={form.facility_id}
              onChange={(e) => setForm((prev) => ({ ...prev, facility_id: e.target.value }))}
            />
          </label>
          <label>
            subject_id
            <input
              value={form.subject_id}
              onChange={(e) => setForm((prev) => ({ ...prev, subject_id: e.target.value }))}
            />
          </label>
          <label>
            reader_id
            <input
              value={form.reader_id}
              onChange={(e) => setForm((prev) => ({ ...prev, reader_id: e.target.value }))}
            />
          </label>
          <label>
            humidity_level
            <input
              type="number"
              value={form.humidity_level}
              onChange={(e) => setForm((prev) => ({ ...prev, humidity_level: e.target.value }))}
            />
          </label>
        </div>
        <div className="icsn-console-actions">
          <button type="button" onClick={injectSample}>샘플 주입</button>
          <button type="button" onClick={flushQueue}>단절 큐 재전송</button>
        </div>
      </section>

      <section className="icsn-console-card">
        <h2>3) 단절 큐(최근 20건)</h2>
        <table>
          <thead>
            <tr>
              <th>event_id</th>
              <th>subject_id</th>
              <th>sensor_mode</th>
              <th>humidity</th>
              <th>event_time</th>
              <th>queued_at</th>
            </tr>
          </thead>
          <tbody>
            {queueItems.length === 0 ? (
              <tr>
                <td colSpan={6}>큐 데이터 없음</td>
              </tr>
            ) : (
              queueItems.map((item) => (
                <tr key={`${item.payload?.event_id || 'unknown'}-${item.queued_at || ''}`}>
                  <td>{item.payload?.event_id}</td>
                  <td>{item.payload?.subject_id}</td>
                  <td>{item.payload?.sensor_mode}</td>
                  <td>{item.payload?.humidity_level ?? '-'}</td>
                  <td>{formatDateTime(item.payload?.event_time)}</td>
                  <td>{formatDateTime(item.queued_at)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </section>

      <section className="icsn-console-card">
        <h2>4) FHIR Outbox (최근 20건)</h2>
        <table>
          <thead>
            <tr>
              <th>outbox_id</th>
              <th>subject_id</th>
              <th>metric_key</th>
              <th>status</th>
              <th>effective_at</th>
              <th>action</th>
            </tr>
          </thead>
          <tbody>
            {outboxItems.length === 0 ? (
              <tr>
                <td colSpan={6}>Outbox 데이터 없음</td>
              </tr>
            ) : (
              outboxItems.map((item) => (
                <tr key={item.outbox_id}>
                  <td>{item.outbox_id}</td>
                  <td>{item.subject_id}</td>
                  <td>{item.metric_key}</td>
                  <td>{item.status}</td>
                  <td>{formatDateTime(item.effective_at)}</td>
                  <td>
                    <button
                      type="button"
                      onClick={() => ackOutbox(item.outbox_id)}
                      disabled={item.status === 'acked'}
                    >
                      ACK
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </section>

      <section className="icsn-console-card">
        <h2>5) 위험신호 / 케어 우선순위</h2>
        <div className="icsn-console-split">
          <div>
            <h3>Risk Signals</h3>
            <table>
              <thead>
                <tr>
                  <th>subject_id</th>
                  <th>signal_code</th>
                  <th>risk_level</th>
                  <th>score</th>
                  <th>created_at</th>
                </tr>
              </thead>
              <tbody>
                {riskItems.length === 0 ? (
                  <tr>
                    <td colSpan={5}>데이터 없음</td>
                  </tr>
                ) : (
                  riskItems.map((item) => (
                    <tr key={`${item.id}-${item.created_at}`}>
                      <td>{item.subject_id}</td>
                      <td>{item.signal_code}</td>
                      <td>{item.risk_level}</td>
                      <td>{item.score}</td>
                      <td>{formatDateTime(item.created_at)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div>
            <h3>Care Priority (open)</h3>
            <table>
              <thead>
                <tr>
                  <th>id</th>
                  <th>subject_id</th>
                  <th>priority</th>
                  <th>reason</th>
                  <th>action</th>
                </tr>
              </thead>
              <tbody>
                {priorityItems.length === 0 ? (
                  <tr>
                    <td colSpan={5}>데이터 없음</td>
                  </tr>
                ) : (
                  priorityItems.map((item) => (
                    <tr key={item.id}>
                      <td>{item.id}</td>
                      <td>{item.subject_id}</td>
                      <td>{item.priority_level}</td>
                      <td>{item.reason}</td>
                      <td>
                        <button type="button" onClick={() => ackPriority(item.id)}>ACK</button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
}

export default IcsnConsolePage;
